// Light / dark for visuals — ONE answer, taken from the surface a visual is
// actually drawn on.
//
// A visual is mounted in very different places: the Run view and the
// downloaded runtime (whose dark/light switch is `body.light-theme`), a
// dashboard tile, a smart page or a published site wearing its own page theme,
// a designer preview. A body class only describes the first two; a page with a
// light theme inside a dark builder would be called dark. So the question is
// asked of the BACKGROUND behind the visual: the first opaque background up the
// tree. That is right on every host, with no host having to tell us.
//
// Two rules follow from it:
//  - CHART CONTENT (plot text, axes, grid cells, map base) follows the surface
//    (`isDarkSurface` / `useDarkSurface`);
//  - CHROME (the ⋮ button, menus, dialogs — often mounted on <body>, outside
//    any visual) wears the host's own tokens (`--color-*`), with the original
//    light colours as fallbacks for a host that defines none (a published
//    site). `VIZ` below and `ensureVizThemeCss` are that rule.

import { useEffect, useLayoutEffect, useState } from 'react'
import { WHITE, isDarkColor, parseRgba, type RGB } from './color'

/** The first (mostly) opaque background behind `el`, or the page's. */
export function surfaceBackground(el: Element | null | undefined): RGB {
  if (typeof window === 'undefined') return WHITE
  let node: Element | null = el ?? null
  while (node && node.nodeType === 1) {
    const c = parseRgba(window.getComputedStyle(node).backgroundColor)
    if (c && c[3] > 0.5) return [c[0], c[1], c[2]]
    node = node.parentElement
  }
  for (const root of [document.body, document.documentElement]) {
    const c = root ? parseRgba(window.getComputedStyle(root).backgroundColor) : null
    if (c && c[3] > 0.5) return [c[0], c[1], c[2]]
  }
  return WHITE
}

/** True when `el` sits on a dark surface (light text wanted). */
export function isDarkSurface(el: Element | null | undefined): boolean {
  return isDarkColor(surfaceBackground(el))
}

/**
 * Whether the visual whose element `getEl` returns sits on a dark surface —
 * re-asked after every render (the element may only now be mounted) and
 * whenever the host flips its theme (a class / style / data-theme change on
 * <html> or <body>), so an open visual follows the switch.
 */
export function useDarkSurface(getEl: () => Element | null | undefined): boolean {
  const [dark, setDark] = useState(false)
  // Every render: cheap (a handful of getComputedStyle calls up the tree) and
  // the only way to catch the element appearing or moving.
  useLayoutEffect(() => {
    const next = isDarkSurface(getEl())
    setDark((prev) => (prev === next ? prev : next))
  })
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined
    const recheck = () => {
      // After the style recalculation the attribute change triggers.
      requestAnimationFrame(() => {
        const next = isDarkSurface(getEl())
        setDark((prev) => (prev === next ? prev : next))
      })
    }
    const mo = new MutationObserver(recheck)
    const attributeFilter = ['class', 'style', 'data-theme']
    mo.observe(document.documentElement, { attributes: true, attributeFilter })
    if (document.body) mo.observe(document.body, { attributes: true, attributeFilter })
    return () => mo.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return dark
}

/**
 * Colours for a visual's own DOM. Each is the page theme's token first (a
 * smart page sets --pg-*), then the host's (--color-*), then the light value a
 * host with neither has always had.
 */
export const VIZ = {
  text: 'var(--pg-text, var(--color-text, #222))',
  heading: 'var(--pg-heading, var(--color-text, #111))',
  muted: 'var(--pg-muted, var(--color-text-secondary, #6b7280))',
  faint: 'var(--pg-faint, var(--color-text-faint, #787878))',
  border: 'var(--pg-border, var(--color-border, #d6dae0))',
  surface: 'var(--pg-surface, var(--color-surface-2, #f8f9fa))',
  raised: 'var(--color-bg-secondary, #fff)',
  hover: 'var(--color-surface-2, #f2f2f2)',
} as const

/**
 * Colours for a visual's CHROME — the ⋮ button and its menu, dialogs: the
 * host's tokens (they sit in the host's UI, often on <body>), falling back to
 * the light values they always had. Shared by NativeVisualMenu and the
 * stylesheet below, so the two ⋮ menus look alike.
 */
export const CHROME = {
  raised: 'var(--color-bg-secondary, #fff)',
  text: 'var(--color-text, #3a4a6b)',
  muted: 'var(--color-text-secondary, #5b6776)',
  border: 'var(--color-border, #d6dae0)',
  hover: 'var(--color-surface-2, #f4f6f9)',
  accent: 'var(--color-accent, #1976d2)',
} as const

const STYLE_ID = 'twasta-viz-theme'

/**
 * The vendored chart engine builds its chrome (the ⋮ button, its menu, the
 * Fields / calculated-field dialogs) as DOM with inline light colours, partly
 * on <body>. A stylesheet beats inline styles only with !important, so these
 * rules re-point those surfaces at the host tokens — unchanged on a light host
 * (the fallbacks are the original colours), dark on a dark one. Leaflet's own
 * controls on a dark map, and the charts' own HTML legend, get the same
 * treatment under `[data-viz-dark]`. Injected once, idempotently.
 */
export function ensureVizThemeCss(): void {
  if (typeof document === 'undefined') return
  const { raised, text, muted, border, hover } = CHROME
  const css = `
.bv-chrome.bv-menu-btn, .bv-chrome.bv-filter-btn, .bv-chrome.bv-fields-btn {
  background: ${raised} !important; color: ${text} !important; border-color: ${border} !important;
}
.bv-chrome.bv-menu, .bv-chrome.bv-popup {
  background: ${raised} !important; color: ${text} !important; border-color: ${border} !important;
}
.bv-chrome.bv-menu button:hover, .bv-chrome.bv-popup button:hover { background: ${hover} !important; }
.bv-fields-dialog-root, .bv-calc-dialog-root { color: ${text} !important; }
.bv-fields-dialog, .bv-fields-dialog-root > div, .bv-calc-dialog-root > div {
  background: ${raised} !important; color: ${text} !important; border-color: ${border} !important;
}
.bv-fields-dialog input, .bv-fields-dialog select, .bv-fields-dialog textarea,
.bv-calc-dialog-root input, .bv-calc-dialog-root select, .bv-calc-dialog-root textarea,
.bv-filter-editor input, .bv-filter-editor select {
  background: ${raised} !important; color: ${text} !important; border-color: ${border} !important;
}
.bv-fd-quad, .bv-fd-quad-body { border-color: ${border} !important; }
.bv-empty-notice { color: ${muted} !important; }
[data-viz-dark] .bv-custom-legend { color: #e6e9ef !important; }
[data-viz-dark] .leaflet-bar a, [data-viz-dark] .leaflet-bar a:hover {
  background: #1f2630; color: #e6e9ef; border-bottom-color: #39414f;
}
[data-viz-dark] .leaflet-control-attribution {
  background: rgba(17, 21, 28, 0.75) !important; color: #b3bcc7 !important;
}
[data-viz-dark] .leaflet-control-attribution a { color: #8fb6ff !important; }
[data-viz-dark] .leaflet-tooltip {
  background: #1f2630; color: #e6e9ef; border-color: #39414f;
}
[data-viz-dark] .leaflet-tooltip-top:before { border-top-color: #1f2630; }
[data-viz-dark] .leaflet-tooltip-bottom:before { border-bottom-color: #1f2630; }
[data-viz-dark] .leaflet-tooltip-left:before { border-left-color: #1f2630; }
[data-viz-dark] .leaflet-tooltip-right:before { border-right-color: #1f2630; }
`
  // One tag; its text is refreshed when present (a module reload in dev).
  const existing = document.getElementById(STYLE_ID)
  if (existing) {
    if (existing.textContent !== css) existing.textContent = css
    return
  }
  const tag = document.createElement('style')
  tag.id = STYLE_ID
  tag.textContent = css
  document.head.appendChild(tag)
}
